import React, { FormEvent, useEffect, useState } from 'react';
import Image from 'next/image';
import { useRouter } from 'next/router';
import Logo from '../../public/images/im3.svg';
import { generateRoomId } from '../../lib/client-utils';

const uiConfig = process.env.NEXT_PUBLIC_UI_CONFIG;
const roomNoun = uiConfig === 'space' ? 'space' : 'meeting';

const ROOM_CODE = /^[A-Za-z0-9_-]+$/;

// Accept a bare code ("p99t-o8pf") or a pasted invite link ("https://…/rooms/p99t-o8pf").
const parseRoomCode = (input: string): string | null => {
  const trimmed = input.trim();
  if (!trimmed) return null;
  const candidate = trimmed.split(/[?#]/)[0].replace(/\/+$/, '').split('/').pop() ?? '';
  return ROOM_CODE.test(candidate) ? candidate : null;
};

const EnterRoom = () => {
  const router = useRouter();
  // Generated after mount so the server-rendered HTML and first client render agree.
  const [roomCode, setRoomCode] = useState('');
  const [joinInput, setJoinInput] = useState('');
  const [joinError, setJoinError] = useState('');

  useEffect(() => {
    setRoomCode(generateRoomId());
  }, []);

  const startRoom = () => {
    if (roomCode) router.push(`/rooms/${roomCode}`);
  };

  const joinRoom = (event: FormEvent) => {
    event.preventDefault();
    const code = parseRoomCode(joinInput);
    if (!code) {
      setJoinError('Enter a room code like p99t-o8pf, or paste an invite link.');
      return;
    }
    router.push(`/rooms/${code}`);
  };

  return (
    <section className="relative z-10 flex min-h-full flex-col px-4 pb-12 pt-8 sm:px-10 lg:px-24">
      <Image className="h-auto w-[96px] xl:w-[128px]" src={Logo} priority alt="IM3" />

      <div className="my-auto w-full max-w-[36rem] pt-16">
        <h1 className="font-SpaceGrotesk text-[2.25rem] leading-[1.05] tracking-[-0.02em] text-im3White sm:text-[3.25rem]">
          Video calls on a public network.
        </h1>
        <p className="mt-5 max-w-[32rem] font-Nunito text-lg leading-relaxed text-[#A3A3A3]">
          Sign in with your wallet, or join as a guest with just a name. Rooms are created the
          moment someone enters.
        </p>

        <div className="mt-12">
          <p className="font-Nunito text-sm text-[#8A8A8A]">Your new {roomNoun}</p>
          <div className="mt-2 flex items-center gap-3">
            <output
              aria-live="polite"
              className="room-code font-SpaceGrotesk text-[2.75rem] leading-none tracking-[0.04em] text-im3White sm:text-[4rem]"
            >
              {roomCode || ' '}
            </output>
            <button
              type="button"
              onClick={() => setRoomCode(generateRoomId())}
              className="rounded-full border border-white/15 p-2.5 text-[#A3A3A3] transition-colors hover:border-white/40 hover:text-im3White focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-im3Red"
              aria-label="Generate a different room code"
              title="Generate a different room code"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M20 11a8 8 0 1 0-2.34 5.66M20 4v7h-7"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>

          <button
            type="button"
            onClick={startRoom}
            disabled={!roomCode}
            className="mt-6 h-14 w-full rounded-full bg-im3Red px-8 font-SpaceGrotesk text-base text-im3Black transition-[filter] hover:brightness-110 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-im3White disabled:opacity-60 sm:w-auto"
          >
            Start {roomNoun}
          </button>
        </div>

        <form onSubmit={joinRoom} className="mt-12 border-t border-white/10 pt-8" noValidate>
          <label htmlFor="room-code" className="font-Nunito text-sm text-[#8A8A8A]">
            Have a code or invite link?
          </label>
          <div className="mt-2 flex flex-col gap-3 sm:flex-row">
            <input
              id="room-code"
              value={joinInput}
              onChange={(e) => {
                setJoinInput(e.target.value);
                if (joinError) setJoinError('');
              }}
              placeholder="p99t-o8pf"
              autoComplete="off"
              spellCheck={false}
              aria-invalid={!!joinError}
              aria-describedby={joinError ? 'room-code-error' : undefined}
              className="h-12 w-full min-w-0 rounded-full border border-white/15 sm:flex-1 bg-transparent px-5 font-SpaceGrotesk text-im3White placeholder:text-[#5C5C5C] focus:border-white/50 focus:outline-none"
            />
            <button
              type="submit"
              className="h-12 rounded-full border border-white/25 px-7 font-SpaceGrotesk text-im3White transition-colors hover:border-white/60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-im3Red"
            >
              Join
            </button>
          </div>
          {joinError && (
            <p id="room-code-error" role="alert" className="mt-3 font-Nunito text-sm text-im3Red">
              {joinError}
            </p>
          )}
        </form>
      </div>
    </section>
  );
};

export default EnterRoom;
